import clsx from 'clsx'
import Image from 'next/image'
import Link from 'next/link'
import { Fragment, useState } from 'react'
import TrialLessonBannerDesktop from './images/TrialLessonBannerDesktop.jpg'
import TrialLessonBannerMobile from './images/TrialLessonBannerMobile.jpg'
import { FahrlehrerCh } from '@/src/@types/blinkadmin'
import Button from '@/src/components/core/Button'
import { Select } from '@/src/components/core/Select'
import Picture from '@/src/components/elements/Picture'
import ContactForm from '@/src/components/flch/components/ContactForm'
import Container from '@/src/components/layout/Container'

const BLINK_URL = 'https://blinkdrive.ch'

const blinkOffers = [
  { label: 'Motorrad', slug: 'motorradgrundkurs' },
  { label: 'VKU', slug: 'vku' },
  { label: 'Nothelferkurs', slug: 'nothelferkurs' },
]

const TrialLessonCta = ({
  name,
  slug,
  avatar,
  locations,
  selectedLocation,
}: FahrlehrerCh.TeacherProfile & {
  selectedLocation: FahrlehrerCh.TeacherProfileLocation
}) => {
  const [locationId, setLocationId] = useState(selectedLocation.id)

  const bookingUrl = `/probelektion?instructorContext=${slug}&location=${locationId}`

  const activeLocation =
    locations.find((l) => l.id === locationId) ?? selectedLocation

  const rest = locations.filter((l) => l.id !== selectedLocation.id)
  const sortedLocations = [
    selectedLocation,
    ...rest.filter((l) => l.hasBlinkLandingpage),
    ...rest.filter((l) => !l.hasBlinkLandingpage),
  ]

  return (
    <section id="booking" className="overflow-hidden pt-20 md:pt-24">
      <div className="relative">
        <Image
          src={TrialLessonBannerMobile}
          alt=""
          fill
          className="top-20! object-cover object-bottom md:hidden"
          aria-hidden
        />
        <Image
          src={TrialLessonBannerDesktop}
          alt=""
          fill
          className="hidden object-cover object-right md:block"
          aria-hidden
        />
        <div
          className={clsx(
            'absolute inset-0 bg-linear-to-b from-white to-transparent md:bg-linear-to-r md:from-white md:to-transparent',
            activeLocation.bookable
              ? 'from-60% md:from-30%'
              : 'from-85% md:from-45%'
          )}
        />
        <Container className="relative z-10">
          <div className="flex flex-col items-center pt-8 pb-32 md:max-w-100 md:items-start md:py-16">
            {avatar && (
              <div className="relative mb-5 size-32 overflow-hidden rounded-full shadow-md ring-2 ring-white">
                <Picture
                  permalink={avatar.url}
                  alt={name}
                  fill
                  className="object-cover"
                  sizes="500px"
                />
              </div>
            )}
            <div>
              <h2 className="text-center text-4xl font-black md:text-left md:text-10xl">
                Mit {name} durchstarten?
              </h2>
              <p className="mt-2 text-center md:text-left">
                {activeLocation.bookable
                  ? 'Buche jetzt deine erste Probelektion online.'
                  : `${name} nimmt am Standort ${activeLocation.name} zurzeit keine Onlinebuchungen entgegen. Schreib uns kurz und wir organisieren deine Probelektion.`}
              </p>
            </div>
            <div className="mt-8 w-full space-y-4">
              {locations.length > 1 ? (
                <Select
                  label="Standort"
                  labelType="block"
                  color="white-blue"
                  value={locationId}
                  onChange={(e) => setLocationId(Number(e.target.value))}
                  unselectable={false}
                >
                  {sortedLocations.map((loc) => (
                    <option key={loc.id} value={loc.id}>
                      {loc.name}
                    </option>
                  ))}
                </Select>
              ) : (
                <div>
                  <p className="mb-1 text-left text-sm text-gray-600">
                    Standort
                  </p>
                  <p className="rounded-sm border border-blue-125 bg-white px-3 py-3.75 text-sm">
                    {selectedLocation.name}
                  </p>
                </div>
              )}
            </div>
            {activeLocation.bookable ? (
              <Button
                as={Link}
                href={bookingUrl}
                version="blue"
                className="mt-8 w-full md:w-auto"
              >
                Probelektion buchen
              </Button>
            ) : (
              <div className="mt-6 w-full">
                <ContactForm
                  context={`Profil ${name}, Standort ${activeLocation.name}`}
                />
              </div>
            )}
          </div>
        </Container>
      </div>
      <Container className="mt-24 md:mt-8">
        <p className="text-sm">
          {name} unterrichtet für die{' '}
          <Link
            href={`${BLINK_URL}/fahrschule-${selectedLocation.slug}`}
            target="_blank"
            className="font-bold text-blue-700"
          >
            BLINK Fahrschule in {selectedLocation.name}
          </Link>
          . Du findest dort weitere Angebote in den Bereichen{' '}
          {blinkOffers.map((offer, index) => (
            <Fragment key={offer.slug}>
              {index > 0 && (index === blinkOffers.length - 1 ? ' und ' : ', ')}
              <Link
                href={`${BLINK_URL}/${offer.slug}-${selectedLocation.slug}`}
                target="_blank"
                className="font-bold text-blue-700"
              >
                {offer.label}
              </Link>
            </Fragment>
          ))}
          .
        </p>
      </Container>
    </section>
  )
}

export default TrialLessonCta
